import { logger } from '~/utils/logger'

const getSuppressionThreshold = surveyCapitalized => {
  return surveyCapitalized === 'Parent' ? 0.15 : 0.3
}

const getSuppression = (constructLabel, surveyCapitalized, y, qNumber) => {
  const yearSetOpt = y ? `, [Flag_DWS_${y.toUpperCase()}]={1}` : ''
  const yearSet = y ? `{<[Flag_DWS_${y.toUpperCase()}]={1}>}` : ''
  const threshold = getSuppressionThreshold(surveyCapitalized)
  /**
   * Note this is using a hardcoded threshold (25% for student/teachers and 10% for parents).
   * This will change in upcoming years, but the old rates will still be used for their corresponding years
   * (e.g., 15% for 21-22 parents, but 10% for 20-21)
   * Suggestion, create a new fields in the app: DWS_Threshold_STUDENT, DWS_Threshold_PARENT, DWS_Threshold_TEACHER that
   * are linked specific to the school year. Then, replace ${threshold} with Max(${yearSet}DWS_Enrolled_${surveyCapitalized})),
   * which, using the set analysis from ${yearSet} should pull just the appropriate threshold.
   */
  let measure = `(Sum(${yearSet}DWS_Responded_${surveyCapitalized}) / Sum(${yearSet}DWS_Enrolled_${surveyCapitalized})) >= ${threshold}`
  if (constructLabel && constructLabel !== 'any' && constructLabel !== 'all') {
    measure =
      measure +
      'and ' +
      (qNumber
        ? `Count(${yearSet}[DWS_${constructLabel.toLowerCase()}_q_${qNumber}_response]) >= 5`
        : `Sum({<[DWS_Survey]={'${surveyCapitalized.toUpperCase()}'} ${yearSetOpt}>} [DWS_${constructLabel.toLowerCase()}_responded]) >= 5`)
  }
  return measure
}

//!!!IMPORTANT!!! - MEASURE NAMES MUST BE UNIQUE FOR LOOKUP FUNCTIONS TO WORK PROPERLY (OTHERWISE THE FIRST KPI WITH THE FOUND NAME WILL BE RETURNED WHEN USING GETTERS)
// Note that for 2021-22 Camelot Academy did not have enrollment on Oct 1, so we manually add here
const kpiMeasures = {
  //47 measures
  overview: [
    {
      qDef: {
        qLabel: 'Total Schools',
        qDef: `Count({<[Flag_Oct1_CY]={1}, [Sector]-={'Non Public Special Education','Pre-K','Other'}>} distinct [%ULCS_SY])`
      }
    },
    {
      qDef: {
        qLabel: 'District Total Enrollment',
        qDef:
          "=Sum({<[Flag_Oct1_CY]={1}, [Sector]={'District'}>} Enrolled_Oct1)"
      }
    },
    {
      qDef: {
        qLabel: 'District Total Schools',
        qDef:
          "=Count({<[Flag_Oct1_CY]={1}, [Sector]={'District'}>} distinct [%ULCS_SY])"
      }
    },
    {
      qDef: {
        qLabel: 'Charter Total Enrollment',
        qDef: "=Sum({<[Flag_Oct1_CY]={1}, [Sector]={'Charter'}>} Enrolled_Oct1)"
      }
    },
    {
      qDef: {
        qLabel: 'Charter Total Schools',
        qDef:
          "=Count({<[Flag_Oct1_CY]={1}, [Sector]={'Charter'}>} distinct [%ULCS_SY])"
      }
    },
    {
      qDef: {
        qLabel: 'Alternative Total Enrollment',
        qDef: `=Sum({<[Flag_Oct1_CY]={1}, [Sector]={'Alternative'}>} Enrolled_Oct1)`
      }
    },
    {
      qDef: {
        qLabel: 'Alternative Total Schools',
        qDef: `=Count({<[Flag_Oct1_CY]={1}, [Sector]={'Alternative'}>} distinct [%ULCS_SY])`
      }
    },
    {
      qDef: {
        qLabel: 'Other Total Enrollment',
        qDef: `Sum({<[Flag_Oct1_CY]={1}>} Enrolled_Oct1) -
            (
              Sum({<[Flag_Oct1_CY]={1}, [Sector]={'District'}>} Enrolled_Oct1) +
              Sum({<[Flag_Oct1_CY]={1}, [Sector]={'Alternative'}>} Enrolled_Oct1) +
              Sum({<[Flag_Oct1_CY]={1}, [Sector]={'Charter'}>} Enrolled_Oct1) +
              Sum({<[Flag_Oct1_CY]={1}, SubSector={'Cyber Charters'}>} Enrolled_Oct1)
              )
            `
      }
    },
    {
      qDef: {
        qLabel: 'Cyber Charter Total Enrollment',
        qDef: `Sum({<[Flag_Oct1_CY]={1}, SubSector={'Cyber Charters'}>} Enrolled_Oct1)`
      }
    },
    {
      qDef: {
        qLabel: 'Other Total Schools',
        qDef: `Count({<[Flag_Oct1_CY]={1}>} distinct [%ULCS_SY])
          -
          Count({<[Flag_Oct1_CY]={1}, [Sector]-={'Non Public Special Education','Pre-K','Other'}>} distinct [%ULCS_SY])
          `
      }
    },
    {
      qDef: {
        qLabel: 'Enrollment',
        qDef: '=Sum({<[Flag_Oct1_CY]={1}>} Enrolled_Oct1)'
      }
    },
    {
      qDef: {
        qLabel: 'High Attendance CY',
        qDef: '=Avg({<[Flag_Climate_CY]={1}>} Attendance_95more)'
      }
    },
    {
      qDef: {
        qLabel: 'High Attendance Count CY',
        qDef: '=Count({<[Flag_Climate_CY]={1}>} Attendance_95more)'
      }
    },
    {
      qDef: {
        qLabel: 'High Attendance PY',
        qDef: '=Avg({<[Flag_Climate_PY]={1}>} Attendance_95more)'
      }
    },
    {
      qDef: {
        qLabel: 'Regular Attendance CY',
        qDef: '=Avg({<[Flag_Climate_CY]={1}>} Attendance_90more)'
      }
    },
    {
      qDef: {
        qLabel: 'Regular Attendance Count CY',
        qDef: '=Count({<[Flag_Climate_CY]={1}>} Attendance_90more)'
      }
    },
    {
      qDef: {
        qLabel: 'Regular Attendance PY',
        qDef: '=Avg({<[Flag_Climate_PY]={1}>} Attendance_90more)'
      }
    },
    {
      qDef: {
        qLabel: 'Positive Behavior CY',
        qDef: '=Avg({<[Flag_Climate_OSS_CY]={1}>} OSS_Zero)'
      }
    },
    {
      qDef: {
        qLabel: 'Positive Behavior Count CY',
        qDef: '=Count({<[Flag_Climate_OSS_CY]={1}>} OSS_Zero)'
      }
    },
    {
      qDef: {
        qLabel: 'Positive Behavior PY',
        qDef: '=Avg({<[Flag_Climate_OSS_PY]={1}>} OSS_Zero)'
      }
    },
    {
      qDef: {
        qLabel: 'PSSA ELA Test CY',
        qDef: '=Avg({<[Flag_StudentMetrics_CY]={1}>} PSSA_ELA_G3_ProfAdv)'
      }
    },
    {
      qDef: {
        qLabel: 'PSSA ELA Test Count CY',
        qDef: '=Count({<[Flag_StudentMetrics_CY]={1}>} PSSA_ELA_G3_ProfAdv)'
      }
    },
    {
      qDef: {
        qLabel: 'PSSA ELA Test PY',
        qDef: '=Avg({<[Flag_StudentMetrics_PY]={1}>} PSSA_ELA_G3_ProfAdv)'
      }
    },
    {
      qDef: {
        qLabel: '4 Year Cohort Graduation CY',
        qDef: '=Avg({<[Flag_CC_CY]={1}>} Graduation_4Years)'
      }
    },
    {
      qDef: {
        qLabel: '4 Year Cohort Graduation Count CY',
        qDef: '=Count({<[Flag_CC_CY]={1}>} Graduation_4Years)'
      }
    },
    {
      qDef: {
        qLabel: '4 Year Cohort Graduation PY',
        qDef: '=Avg({<[Flag_CC_PY]={1}>} Graduation_4Years)'
      }
    },
    {
      qDef: {
        qLabel: 'College Matriculation CY',
        qDef: '=Avg({<[Flag_CC_CY]={1}>} FFM_Matriculated)'
      }
    },
    {
      qDef: {
        qLabel: 'College Matriculation Count CY',
        qDef: '=Count({<[Flag_CC_CY]={1}>} FFM_Matriculated)'
      }
    },
    {
      qDef: {
        qLabel: 'College Matriculation PY',
        qDef: '=Avg({<[Flag_CC_PY]={1}>} FFM_Matriculated)'
      }
    },
    {
      qDef: {
        qLabel: 'PSSA/Keystone Math/Alg1 Performance Sum (CY)',
        qDef: '=Sum({<[Flag_StudentMetrics_CY]={1}>} PSSAKeystone_Math_ProfAdv)'
      }
    },
    {
      qDef: {
        qLabel: 'PSSA/Keystone Math/Alg1 Performance Count (CY)',
        qDef:
          '=Count({<[Flag_StudentMetrics_CY]={1}>} PSSAKeystone_Math_ProfAdv)'
      }
    },
    {
      qDef: {
        qLabel: 'PSSA/Keystone Math/Alg1 Performance Sum (PY)',
        qDef: '=Sum({<[Flag_StudentMetrics_PY]={1}>} PSSAKeystone_Math_ProfAdv)'
      }
    },
    {
      qDef: {
        qLabel: 'PSSA/Keystone Math/Alg1 Performance Count (PY)',
        qDef:
          '=Count({<[Flag_StudentMetrics_PY]={1}>} PSSAKeystone_Math_ProfAdv)'
      }
    },
    {
      qDef: {
        qLabel: 'PSSA/Keystone Math/Alg1 Performance Title',
        qDef:
          "=If(IsNull(EmptyIsNull(Concat(distinct {<[Flag_StudentMetrics_CPY]={1}>} PSSAKeystone_Math_Test))),'Assessment',Concat(distinct {<[Flag_StudentMetrics_CPY]={1}>} PSSAKeystone_Math_Test,' & ', Match(PSSAKeystone_Math_Test,'PSSA','Keystone')))"
      }
    },
    {
      qDef: {
        qLabel: 'PSSA/Keystone ELA/Lit Performance Sum (CY)',
        qDef: '=Sum({<[Flag_StudentMetrics_CY]={1}>} PSSAKeystone_ELA_ProfAdv)'
      }
    },
    {
      qDef: {
        qLabel: 'PSSA/Keystone ELA/Lit Performance Count (CY)',
        qDef:
          '=Count({<[Flag_StudentMetrics_CY]={1}>} PSSAKeystone_ELA_ProfAdv)'
      }
    },
    {
      qDef: {
        qLabel: 'PSSA/Keystone ELA/Lit Performance Sum (PY)',
        qDef: '=Sum({<[Flag_StudentMetrics_PY]={1}>} PSSAKeystone_ELA_ProfAdv)'
      }
    },
    {
      qDef: {
        qLabel: 'PSSA/Keystone ELA/Lit Performance Count (PY)',
        qDef:
          '=Count({<[Flag_StudentMetrics_PY]={1}>} PSSAKeystone_ELA_ProfAdv)'
      }
    },
    {
      qDef: {
        qLabel: 'PSSA/Keystone ELA/Lit Performance Title',
        qDef:
          "=If(IsNull(EmptyIsNull(Concat(distinct {<[Flag_StudentMetrics_CPY]={1}>} PSSAKeystone_ELA_Test))),'Assessment',Concat(distinct {<[Flag_StudentMetrics_CPY]={1}>} PSSAKeystone_ELA_Test,' & ', Match(PSSAKeystone_ELA_Test,'PSSA','Keystone')))"
      }
    },
    {
      qDef: {
        qLabel: 'Assistant Superintendents',
        qDef:
          "=If(IsNull(EmptyIsNull(Concat({<Flag_CurrentSchoolDirectory={1}>} distinct [Current AssistantSuperintendent], ', '))),'-',Concat({<Flag_CurrentSchoolDirectory={1}>} distinct [Current AssistantSuperintendent], ', '))"
      }
    },
    {
      qDef: {
        qLabel: 'Elementary Total Schools',
        qDef: `=Count({<[Flag_Oct1_CY]={1}, [SchoolLevel]={'Elementary'}>} distinct [%ULCS_SY])`
      }
    },
    {
      qDef: {
        qLabel: 'Elementary-Middle Total Schools',
        qDef: `=Count({<[Flag_Oct1_CY]={1}, [SchoolLevel]={'Elementary-Middle'}>} distinct [%ULCS_SY])`
      }
    },
    {
      qDef: {
        qLabel: 'Elementary-High Total Schools',
        qDef: `=Count({<[Flag_Oct1_CY]={1}, [SchoolLevel]={'Elementary-High'}>} distinct [%ULCS_SY])`
      }
    },
    {
      qDef: {
        qLabel: 'Elementary-Middle-High Total Schools',
        qDef: `=Count({<[Flag_Oct1_CY]={1}, [SchoolLevel]={'Elementary-Middle-High'}>} distinct [%ULCS_SY])`
      }
    },
    {
      qDef: {
        qLabel: 'Middle Total Schools',
        qDef: `=Count({<[Flag_Oct1_CY]={1}, [SchoolLevel]={'Middle'}>} distinct [%ULCS_SY])`
      }
    },
    {
      qDef: {
        qLabel: 'Middle-High Total Schools',
        qDef: `=Count({<[Flag_Oct1_CY]={1}, [SchoolLevel]={'Middle-High'}>} distinct [%ULCS_SY])`
      }
    },
    {
      qDef: {
        qLabel: 'High Total Schools',
        qDef: `=Count({<[Flag_Oct1_CY]={1}, [SchoolLevel]={'High'}>} distinct [%ULCS_SY])`
      }
    },
    {
      qDef: {
        qLabel: 'Transition Total Schools',
        qDef: `=Count({<[Flag_Oct1_CY]={1}, [SchoolLevel]={'Transition/Overage School'}>} distinct [%ULCS_SY])`
      }
    },

    {
      qDef: {
        qLabel: 'Teacher Attendance CY',
        qDef: `=Avg({<[SPR.MetricID]={'TEACHER_ATTENDANCE_90'},Flag_SPREE_CY={1}>}[SPR.Score])`
      }
    },
    {
      qDef: {
        qLabel: 'Teacher Attendance # Employees CY',
        qDef: `=sum({<[Flag_CC_CY]={1}>}"# of Employees")`
      }
    },
    {
      qDef: {
        qLabel: 'Teacher Attendance # 90more CY',
        qDef: `=sum({<[Flag_CC_CY]={1}>}"# of Employees with 90%+ Attendance")`
      }
    },
    {
      qDef: {
        qLabel: 'Teacher Attendance PY',
        qDef: `=Avg({<[SPR.MetricID]={'TEACHER_ATTENDANCE_90'},Flag_SPREE_PY={1}>}[SPR.Score])`
      }
    },
    {
      qDef: {
        qLabel: 'Teacher Attendance # Employees PY',
        qDef: `=sum({<[Flag_CC_PY]={1}>}"# of Employees")`
      }
    },
    {
      qDef: {
        qLabel: 'Teacher Attendance # 90more PY',
        qDef: `=sum({<[Flag_CC_PY]={1}>}"# of Employees with 90%+ Attendance")`
      }
    }
  ],
  // 1 mesure (for now)
  spree: [
    {
      qDef: {
        qLabel: 'SPREE - % Meeting CY',
        qDef:
          "=Avg({<[SPR.MetricID]={'overall'}, [Flag_SPREE_CY]={1}>} [SPREE.Percent_Improving])"
      }
    }
  ],
  //10 measures
  aepr: [
    {
      qDef: {
        qLabel: 'AEPR Sum (CY)',
        qDef:
          "=Sum({<AEPR.MetricID={'OVERALL'},[Flag_AEPR_CY]={1}>} AEPR.Score)"
      }
    },
    {
      qDef: {
        qLabel: 'AEPR Count (CY)',
        qDef:
          "=Count({<AEPR.MetricID={'OVERALL'},[Flag_AEPR_CY]={1}>} AEPR.Score)"
      }
    },
    {
      qDef: {
        qLabel: 'AEPR Sum (PY)',
        qDef:
          "=Sum({<AEPR.MetricID={'OVERALL'},[Flag_AEPR_PY]={1}>} AEPR.Score)"
      }
    },
    {
      qDef: {
        qLabel: 'AEPR Count (PY)',
        qDef:
          "=Count({<AEPR.MetricID={'OVERALL'},[Flag_AEPR_PY]={1}>} AEPR.Score)"
      }
    },
    {
      qDef: {
        qLabel: 'AEPR Achievement CY',
        qDef:
          "=Avg({<[AEPR.MetricID]={'ACHIEVEMENT'}, [Flag_AEPR_CY]={1}>} AEPR.Score)"
      }
    },
    {
      qDef: {
        qLabel: 'AEPR Achievement PY',
        qDef:
          "=Avg({<[AEPR.MetricID]={'ACHIEVEMENT'}, [Flag_AEPR_PY]={1}>} AEPR.Score)"
      }
    },
    {
      qDef: {
        qLabel: 'AEPR Progress CY',
        qDef:
          "=Avg({<[AEPR.MetricID]={'PROGRESS'}, [Flag_AEPR_CY]={1}>} AEPR.Score)"
      }
    },
    {
      qDef: {
        qLabel: 'AEPR Progress PY',
        qDef:
          "=Avg({<[AEPR.MetricID]={'PROGRESS'}, [Flag_AEPR_PY]={1}>} AEPR.Score)"
      }
    },
    {
      qDef: {
        qLabel: 'AEPR Climate CY',
        qDef:
          "=Avg({<[AEPR.MetricID]={'CLIMATE'}, [Flag_AEPR_CY]={1}>} AEPR.Score)"
      }
    },
    {
      qDef: {
        qLabel: 'AEPR Climate PY',
        qDef:
          "=Avg({<[AEPR.MetricID]={'CLIMATE'}, [Flag_AEPR_PY]={1}>} AEPR.Score)"
      }
    },
    {
      qDef: {
        qLabel: 'AEPR CollegeCareer CY',
        qDef:
          "=Avg({<[AEPR.MetricID]={'COLLEGE & CAREER'}, [Flag_AEPR_CY]={1}>} AEPR.Score)"
      }
    },
    {
      qDef: {
        qLabel: 'AEPR CollegeCareer PY',
        qDef:
          "=Avg({<[AEPR.MetricID]={'COLLEGE & CAREER'}, [Flag_AEPR_PY]={1}>} AEPR.Score)"
      }
    }
  ],
  //16 measures
  mathsci: [
    {
      qDef: {
        qLabel: 'Algebra Sum (CY)',
        qDef: '=Sum({<[Flag_StudentMetrics_CY]={1}>} Keystone_Math_ProfAdv)'
      }
    },
    {
      qDef: {
        qLabel: 'Algebra Count (CY)',
        qDef: '=Count({<[Flag_StudentMetrics_CY]={1}>} Keystone_Math_ProfAdv)'
      }
    },
    {
      qDef: {
        qLabel: 'Algebra Sum (PY)',
        qDef: '=Sum({<[Flag_StudentMetrics_PY]={1}>} Keystone_Math_ProfAdv)'
      }
    },
    {
      qDef: {
        qLabel: 'Algebra Count (PY)',
        qDef: '=Count({<[Flag_StudentMetrics_PY]={1}>} Keystone_Math_ProfAdv)'
      }
    },
    {
      qDef: {
        qLabel: 'Science Sum (CY)',
        qDef: '=Sum({<[Flag_StudentMetrics_CY]={1}>} Keystone_Science_ProfAdv)'
      }
    },
    {
      qDef: {
        qLabel: 'Science Count (CY)',
        qDef:
          '=Count({<[Flag_StudentMetrics_CY]={1}>} Keystone_Science_ProfAdv)'
      }
    },
    {
      qDef: {
        qLabel: 'Science Sum (PY)',
        qDef: '=Sum({<[Flag_StudentMetrics_PY]={1}>} Keystone_Science_ProfAdv)'
      }
    },
    {
      qDef: {
        qLabel: 'Science Count (PY)',
        qDef:
          '=Count({<[Flag_StudentMetrics_PY]={1}>} Keystone_Science_ProfAdv)'
      }
    },
    {
      qDef: {
        qLabel: 'PSSA Math Sum (CY)',
        qDef: '=Sum({<[Flag_StudentMetrics_CY]={1}>} PSSA_Math_ProfAdv)'
      }
    },
    {
      qDef: {
        qLabel: 'PSSA Math Count (CY)',
        qDef: '=Count({<[Flag_StudentMetrics_CY]={1}>} PSSA_Math_ProfAdv)'
      }
    },
    {
      qDef: {
        qLabel: 'PSSA Math Sum (PY)',
        qDef: '=Sum({<[Flag_StudentMetrics_PY]={1}>} PSSA_Math_ProfAdv)'
      }
    },
    {
      qDef: {
        qLabel: 'PSSA Math Count (PY)',
        qDef: '=Count({<[Flag_StudentMetrics_PY]={1}>} PSSA_Math_ProfAdv)'
      }
    },
    {
      qDef: {
        qLabel: 'PSSA Science Sum (CY)',
        qDef: '=Sum({<[Flag_StudentMetrics_CY]={1}>} PSSA_Science_ProfAdv)'
      }
    },
    {
      qDef: {
        qLabel: 'PSSA Science Count (CY)',
        qDef: '=Count({<[Flag_StudentMetrics_CY]={1}>} PSSA_Science_ProfAdv)'
      }
    },
    {
      qDef: {
        qLabel: 'PSSA Science Sum (PY)',
        qDef: '=Sum({<[Flag_StudentMetrics_PY]={1}>} PSSA_Science_ProfAdv)'
      }
    },
    {
      qDef: {
        qLabel: 'PSSA Science Count (PY)',
        qDef: '=Count({<[Flag_StudentMetrics_PY]={1}>} PSSA_Science_ProfAdv)'
      }
    }
  ],
  //16 measures
  collegecareer: [
    {
      qDef: {
        qLabel: '9th grade on track Sum (CY)',
        qDef: '=Sum({<[Flag_CC_CY]={1}>} NinthGradeOnTrack_OnTrack)'
      }
    },
    {
      qDef: {
        qLabel: '9th grade on track Count (CY)',
        qDef: '=Count({<[Flag_CC_CY]={1}>} NinthGradeOnTrack_OnTrack)'
      }
    },
    {
      qDef: {
        qLabel: '9th grade on track Sum (PY)',
        qDef: '=Sum({<[Flag_CC_PY]={1}>} NinthGradeOnTrack_OnTrack)'
      }
    },
    {
      qDef: {
        qLabel: '9th grade on track Count (PY)',
        qDef: '=Count({<[Flag_CC_PY]={1}>} NinthGradeOnTrack_OnTrack)'
      }
    },
    {
      qDef: {
        qLabel: '4 year graduation Sum (CY)',
        qDef: '=Sum({<[Flag_CC_CY]={1}>} Graduation_4Years)'
      }
    },
    {
      qDef: {
        qLabel: '4 year graduation Count (CY)',
        qDef: '=Count({<[Flag_CC_CY]={1}>} Graduation_4Years)'
      }
    },
    {
      qDef: {
        qLabel: '4 year graduation Sum (PY)',
        qDef: '=Sum({<[Flag_CC_PY]={1}>} Graduation_4Years)'
      }
    },
    {
      qDef: {
        qLabel: '4 year graduation Count (PY)',
        qDef: '=Count({<[Flag_CC_PY]={1}>} Graduation_4Years)'
      }
    },
    {
      qDef: {
        qLabel: 'First-fall college matriculation Sum (CY)',
        qDef: '=Sum({<[Flag_CC_CY]={1}>}FFM_Matriculated)'
      }
    },
    {
      qDef: {
        qLabel: 'First-fall college matriculation Count (CY)',
        qDef: '=Count({<[Flag_CC_CY]={1}>}FFM_Matriculated)'
      }
    },
    {
      qDef: {
        qLabel: 'First-fall college matriculation Sum (PY)',
        qDef: '=Sum({<[Flag_CC_PY]={1}>} FFM_Matriculated)'
      }
    },
    {
      qDef: {
        qLabel: 'First-fall college matriculation Count (PY)',
        qDef: '=Count({<[Flag_CC_PY]={1}>} FFM_Matriculated)'
      }
    },
    {
      qDef: {
        qLabel: 'College Persistence Sum (CY)',
        qDef: '=Sum({<[Flag_CC_CY]={1}>} Persistence_Persisted)'
      }
    },
    {
      qDef: {
        qLabel: 'College Persistence Count (CY)',
        qDef: '=Count({<[Flag_CC_CY]={1}>} Persistence_Persisted)'
      }
    },
    {
      qDef: {
        qLabel: 'College Persistence Sum (PY)',
        qDef: '=Sum({<[Flag_CC_PY]={1}>} Persistence_Persisted)'
      }
    },
    {
      qDef: {
        qLabel: 'College Persistence Count (PY)',
        qDef: '=Count({<[Flag_CC_PY]={1}>} Persistence_Persisted)'
      }
    }
  ],
  //10 measures
  climate: [
    {
      qDef: {
        qLabel: 'Attendance Sum (CY)',
        qDef: '=Sum({<[Flag_Climate_CY]={1}>} Attendance_95more)'
      }
    },
    {
      qDef: {
        qLabel: 'Attendance Count (CY)',
        qDef: '=Count({<[Flag_Climate_CY]={1}>} Attendance_95more)'
      }
    },
    {
      qDef: {
        qLabel: 'Attendance Sum (PY)',
        qDef: '=Sum({<[Flag_Climate_PY]={1}>} Attendance_95more)'
      }
    },
    {
      qDef: {
        qLabel: 'Attendance Count (PY)',
        qDef: '=Count({<[Flag_Climate_PY]={1}>} Attendance_95more)'
      }
    },
    {
      qDef: {
        qLabel: 'Regular Attendance Sum (CY)',
        qDef: '=Sum({<[Flag_Climate_CY]={1}>} Attendance_90more)'
      }
    },
    {
      qDef: {
        qLabel: 'Regular Attendance Count (CY)',
        qDef: '=Count({<[Flag_Climate_CY]={1}>} Attendance_90more)'
      }
    },
    {
      qDef: {
        qLabel: 'Regular Attendance Sum (PY)',
        qDef: '=Sum({<[Flag_Climate_PY]={1}>} Attendance_90more)'
      }
    },
    {
      qDef: {
        qLabel: 'Regular Attendance Count (PY)',
        qDef: '=Count({<[Flag_Climate_PY]={1}>} Attendance_90more)'
      }
    },
    {
      qDef: {
        qLabel: 'Positive Behavior Sum (CY)',
        qDef: '=Sum({<[Flag_Climate_OSS_CY]={1}>} OSS_Zero)'
      }
    },
    {
      qDef: {
        qLabel: 'Positive Behavior Count (CY)',
        qDef: '=Count({<[Flag_Climate_OSS_CY]={1}>} OSS_Zero)'
      }
    },
    {
      qDef: {
        qLabel: 'Positive Behavior Sum (PY)',
        qDef: '=Sum({<[Flag_Climate_OSS_PY]={1}>} OSS_Zero)'
      }
    },
    {
      qDef: {
        qLabel: 'Positive Behavior Count (PY)',
        qDef: '=Count({<[Flag_Climate_OSS_PY]={1}>} OSS_Zero)'
      }
    },
    {
      qDef: {
        qLabel: 'Serious Incident Rate (CY)',
        qDef: `=(Sum({<[Sector]-={'Charter', 'Non Public Special Education', 'Pre-K', 'Other'},  [Flag_Climate_CY]={1},Gender=,Grade=,Ethnicity=>} Incident)
            / Sum({<[Sector]-={'Charter', 'Non Public Special Education', 'Pre-K', 'Other'},  [Flag_Climate_CY]={1},Gender=,Grade=,Ethnicity=>} [ADM (Yearly)])) * 100`
      }
    },
    {
      qDef: {
        qLabel: 'Serious Incident Rate (PY)',
        qDef: `=(Sum({<[Sector]-={'Charter', 'Non Public Special Education', 'Pre-K', 'Other'},  [Flag_Climate_PY]={1},Gender=,Grade=,Ethnicity=>} Incident)
          / Sum({<[Sector]-={'Charter', 'Non Public Special Education', 'Pre-K', 'Other'},  [Flag_Climate_PY]={1},Gender=,Grade=,Ethnicity=>} [ADM (Yearly)])) * 100`
      }
    }
  ],
  //41 measures
  survey: [
    {
      /* Student Response Rate (year over year) */
      qDef: {
        qLabel: 'Student Response Rate (CY)',
        qDef: '=Sum({<Flag_DWS_CY={1}>}DWS_Responded_Student)'
      }
    },
    {
      qDef: {
        qLabel: 'Student Response Rate (CY) - Denominator',
        qDef: '=Sum({<Flag_DWS_CY={1}>}DWS_Enrolled_Student)'
      }
    },
    {
      qDef: {
        qLabel: 'Student Response Rate (PY)',
        qDef: '=Sum({<Flag_DWS_PY={1}>}DWS_Responded_Student)'
      }
    },
    {
      qDef: {
        qLabel: 'Student Response Rate (PY) - Denominator',
        qDef: '=Sum({<Flag_DWS_PY={1}>}DWS_Enrolled_Student)'
      }
    },
    /* Teacher Response Rate (year over year) */
    {
      qDef: {
        qLabel: 'Teacher Response Rate (CY)',
        qDef: '=Sum({<Flag_DWS_CY={1}>} DWS_Responded_Teacher)'
      }
    },
    {
      qDef: {
        qLabel: 'Teacher Response Rate (CY) - Denominator',
        qDef: 'Sum({<Flag_DWS_CY={1}>} DWS_Enrolled_Teacher)'
      }
    },
    {
      qDef: {
        qLabel: 'Teacher Response Rate (PY)',
        qDef:
          '=Sum({<Flag_DWS_PY={1}>}DWS_Responded_Teacher) / Sum({<Flag_DWS_PY={1}>}DWS_Enrolled_Teacher)'
      }
    },
    /* Support Response Rate (year over year) */
    {
      qDef: {
        qLabel: 'Support Response Rate (CY)',
        qDef: '=Sum({<Flag_DWS_CY={1}>} DWS_Responded_Support)'
      }
    },
    {
      qDef: {
        qLabel: 'Support Response Rate (CY) - Denominator',
        qDef: 'Sum({<Flag_DWS_CY={1}>} DWS_Enrolled_Support)'
      }
    },
    {
      qDef: {
        qLabel: 'Support Response Rate (PY)',
        qDef:
          '=Sum({<Flag_DWS_PY={1}>}DWS_Responded_Support) / Sum({<Flag_DWS_PY={1}>}DWS_Enrolled_Support)'
      }
    },
    /* Parent Response Rate (year over year) */
    {
      qDef: {
        qLabel: 'Parent Response Rate (CY)',
        qDef: '=Sum({<Flag_DWS_CY={1}>}DWS_Responded_Parent)'
      }
    },
    {
      qDef: {
        qLabel: 'Parent Response Rate (CY) - Denominator',
        qDef: '=Sum({<Flag_DWS_CY={1}>}DWS_Enrolled_Parent)'
      }
    },
    {
      qDef: {
        qLabel: 'Parent Response Rate (PY)',
        qDef: '=Sum({<Flag_DWS_PY={1}>}DWS_Responded_Parent)'
      }
    },
    {
      qDef: {
        qLabel: 'Parent Response Rate (PY) - Denominator',
        qDef: '=Sum({<Flag_DWS_PY={1}>}DWS_Enrolled_Parent)'
      }
    },
    {
      qDef: {
        qLabel: 'Student Suppression (CY)',
        qDef: `=if(${getSuppression('', 'Student', 'cy')}, 1, 0)`
      }
    },
    {
      qDef: {
        qLabel: 'Parent Suppression (CY)',
        qDef: `=if(${getSuppression('', 'Parent', 'cy')}, 1, 0)`
      }
    },
    {
      qDef: {
        qLabel: 'Teacher Suppression (CY)',
        qDef: `=if(${getSuppression('', 'Teacher', 'cy')}, 1, 0)`
      }
    },
    /////////////////////////////////// CONSTRUCT QUESTIONS ////////////////////////////////////
    {
      qDef: {
        qLabel: 'Climate Q1 Most Positive (CY)',
        qDef: `=Num(
          if(${getSuppression('climate', 'Student', 'cy', 1)},
            Sum({<[Flag_DWS_CY]={1}>}[DWS_climate_q_1_ishighest])
            /
            Count({<[Flag_DWS_CY]={1}>}[DWS_climate_q_1_response])
          ),'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Climate Q1 Most Positive (PY)',
        qDef: `=Num(
          if(${getSuppression('climate', 'Student', 'py', 1)},
            Sum({<[Flag_DWS_PY]={1}>}[DWS_climate_q_1_ishighest])
            /
            Count({<[Flag_DWS_PY]={1}>}[DWS_climate_q_1_response])
          ),'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Climate Q1 (CY)',
        qDef: `=Num(
          if(${getSuppression('climate', 'Student', 'cy', 1)},
            Count({<[Flag_DWS_CY]={1}>}[DWS_climate_q_1_response])
            /
            Count(total {<[Flag_DWS_CY]={1}>}[DWS_climate_q_1_response])
            ) ,'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Climate Q1 (PY)',
        qDef: `=Num(
          if(${getSuppression('climate', 'Student', 'py', 1)},
            Count({<[Flag_DWS_PY]={1}>}[DWS_climate_q_1_response])
            /
            Count(total {<[Flag_DWS_PY]={1}>}[DWS_climate_q_1_response])
            ) ,'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Climate Q2 Most Positive (CY)',
        qDef: `=Num(
          if(${getSuppression('climate', 'Student', 'cy', 2)},
            Sum({<[Flag_DWS_CY]={1}>}[DWS_climate_q_2_ishighest])
            /
            Count({<[Flag_DWS_CY]={1}>}[DWS_climate_q_2_response])
          ),'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Climate Q2 Most Positive (PY)',
        qDef: `=Num(
          if(${getSuppression('climate', 'Student', 'py', 2)},
            Sum({<[Flag_DWS_PY]={1}>}[DWS_climate_q_2_ishighest])
            /
            Count({<[Flag_DWS_PY]={1}>}[DWS_climate_q_2_response])
          ),'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Climate Q2 (CY)',
        qDef: `=Num(
          if(${getSuppression('climate', 'Student', 'cy', 2)},
            Count({<[Flag_DWS_CY]={1}>}[DWS_climate_q_2_response])
            /
            Count(total {<[Flag_DWS_CY]={1}>}[DWS_climate_q_2_response])
            ) ,'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Climate Q2 (PY)',
        qDef: `=Num(
          if(${getSuppression('climate', 'Student', 'py', 2)},
            Count({<[Flag_DWS_PY]={1}>}[DWS_climate_q_2_response])
            /
            Count(total {<[Flag_DWS_PY]={1}>}[DWS_climate_q_2_response])
            ) ,'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Climate Q3 Most Positive (CY)',
        qDef: `=Num(
          if(${getSuppression('climate', 'Parent', 'cy', 3)},
            Sum({<[Flag_DWS_CY]={1}>}[DWS_climate_q_3_ishighest])
            /
            Count({<[Flag_DWS_CY]={1}>}[DWS_climate_q_3_response])
          ),'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Climate Q3 Most Positive (PY)',
        qDef: `=Num(
          if(${getSuppression('climate', 'Parent', 'py', 3)},
            Sum({<[Flag_DWS_PY]={1}>}[DWS_climate_q_3_ishighest])
            /
            Count({<[Flag_DWS_PY]={1}>}[DWS_climate_q_3_response])
          ),'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Climate Q3 (CY)',
        qDef: `=Num(
          if(${getSuppression('climate', 'Parent', 'cy', 3)},
            Count({<[Flag_DWS_CY]={1}>}[DWS_climate_q_3_response])
            /
            Count(total {<[Flag_DWS_CY]={1}>}[DWS_climate_q_3_response])
            ) ,'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Climate Q3 (PY)',
        qDef: `=Num(
          if(${getSuppression('climate', 'Parent', 'py', 3)},
            Count({<[Flag_DWS_PY]={1}>}[DWS_climate_q_3_response])
            /
            Count(total {<[Flag_DWS_PY]={1}>}[DWS_climate_q_3_response])
            ) ,'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Climate Q3 (PY)',
        qDef: `=Num(
          if(${getSuppression('climate', 'Parent', 'py', 3)},
            Count({<[Flag_DWS_PY]={1}>}[DWS_climate_q_3_response])
            /
            Count(total {<[Flag_DWS_PY]={1}>}[DWS_climate_q_3_response])
            ) ,'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Climate Q4 Most Positive (CY)',
        qDef: `=Num(
          if(${getSuppression('climate', 'Teacher', 'cy', 4)},
            Sum({<[Flag_DWS_CY]={1}>}[DWS_climate_q_4_ishighest])
            /
            Count({<[Flag_DWS_CY]={1}>}[DWS_climate_q_4_response])
          ),'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Climate Q4 Most Positive (PY)',
        qDef: `=Num(
          if(${getSuppression('climate', 'Teacher', 'py', 4)},
            Sum({<[Flag_DWS_PY]={1}>}[DWS_climate_q_4_ishighest])
            /
            Count({<[Flag_DWS_PY]={1}>}[DWS_climate_q_4_response])
          ),'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Climate Q4 (CY)',
        qDef: `=Num(
          if(${getSuppression('climate', 'Teacher', 'cy', 4)},
            Count({<[Flag_DWS_CY]={1}>}[DWS_climate_q_4_response])
            /
            Count(total {<[Flag_DWS_CY]={1}>}[DWS_climate_q_4_response])
            ) ,'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Climate Q4 (PY)',
        qDef: `=Num(
          if(${getSuppression('climate', 'Teacher', 'py', 4)},
            Count({<[Flag_DWS_PY]={1}>}[DWS_climate_q_4_response])
            /
            Count(total {<[Flag_DWS_PY]={1}>}[DWS_climate_q_4_response])
            ) ,'##.0%')`
      }
    },

    {
      qDef: {
        qLabel: 'Instruction Q1 Most Positive (CY)',
        qDef: `=Num(
          if(${getSuppression('instruction', 'Student', 'cy', 1)},
            Sum({<[Flag_DWS_CY]={1}>}[DWS_instruction_q_1_ishighest])
            /
            Count({<[Flag_DWS_CY]={1}>}[DWS_instruction_q_1_response])
          ),'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Instruction Q1 Most Positive (PY)',
        qDef: `=Num(
          if(${getSuppression('instruction', 'Student', 'py', 1)},
            Sum({<[Flag_DWS_PY]={1}>}[DWS_instruction_q_1_ishighest])
            /
            Count({<[Flag_DWS_PY]={1}>}[DWS_instruction_q_1_response])
          ),'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Instruction Q1 (CY)',
        qDef: `=Num(
          if(${getSuppression('instruction', 'Student', 'cy', 1)},
            Count({<[Flag_DWS_CY]={1}>}[DWS_instruction_q_1_response])
            /
            Count(total {<[Flag_DWS_CY]={1}>}[DWS_instruction_q_1_response])
            ) ,'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Instruction Q1 (PY)',
        qDef: `=Num(
          if(${getSuppression('instruction', 'Student', 'py', 1)},
            Count({<[Flag_DWS_PY]={1}>}[DWS_instruction_q_1_response])
            /
            Count(total {<[Flag_DWS_PY]={1}>}[DWS_instruction_q_1_response])
            ) ,'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Instruction Q2 Most Positive (CY)',
        qDef: `=Num(
          if(${getSuppression('instruction', 'Student', 'cy', 2)},
            Sum({<[Flag_DWS_CY]={1}>}[DWS_instruction_q_2_ishighest])
            /
            Count({<[Flag_DWS_CY]={1}>}[DWS_instruction_q_2_response])
          ),'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Instruction Q2 Most Positive (PY)',
        qDef: `=Num(
          if(${getSuppression('instruction', 'Student', 'py', 2)},
            Sum({<[Flag_DWS_PY]={1}>}[DWS_instruction_q_2_ishighest])
            /
            Count({<[Flag_DWS_PY]={1}>}[DWS_instruction_q_2_response])
          ),'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Instruction Q2 (CY)',
        qDef: `=Num(
          if(${getSuppression('instruction', 'Student', 'cy', 2)},
            Count({<[Flag_DWS_CY]={1}>}[DWS_instruction_q_2_response])
            /
            Count(total {<[Flag_DWS_CY]={1}>}[DWS_instruction_q_2_response])
            ) ,'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Instruction Q2 (PY)',
        qDef: `=Num(
          if(${getSuppression('instruction', 'Student', 'py', 2)},
            Count({<[Flag_DWS_PY]={1}>}[DWS_instruction_q_2_response])
            /
            Count(total {<[Flag_DWS_PY]={1}>}[DWS_instruction_q_2_response])
            ) ,'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Instruction Q3 Most Positive (CY)',
        qDef: `=Num(
          if(${getSuppression('instruction', 'Parent', 'cy', 3)},
            Sum({<[Flag_DWS_CY]={1}>}[DWS_instruction_q_3_ishighest])
            /
            Count({<[Flag_DWS_CY]={1}>}[DWS_instruction_q_3_response])
          ),'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Instruction Q3 Most Positive (PY)',
        qDef: `=Num(
          if(${getSuppression('instruction', 'Parent', 'py', 3)},
            Sum({<[Flag_DWS_PY]={1}>}[DWS_instruction_q_3_ishighest])
            /
            Count({<[Flag_DWS_PY]={1}>}[DWS_instruction_q_3_response])
          ),'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Instruction Q3 (CY)',
        qDef: `=Num(
          if(${getSuppression('instruction', 'Parent', 'cy', 3)},
            Count({<[Flag_DWS_CY]={1}>}[DWS_instruction_q_3_response])
            /
            Count(total {<[Flag_DWS_CY]={1}>}[DWS_instruction_q_3_response])
            ) ,'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Instruction Q3 (PY)',
        qDef: `=Num(
          if(${getSuppression('instruction', 'Parent', 'py', 3)},
            Count({<[Flag_DWS_PY]={1}>}[DWS_instruction_q_3_response])
            /
            Count(total {<[Flag_DWS_PY]={1}>}[DWS_instruction_q_3_response])
            ) ,'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Instruction Q3 (PY)',
        qDef: `=Num(
          if(${getSuppression('instruction', 'Parent', 'py', 3)},
            Count({<[Flag_DWS_PY]={1}>}[DWS_instruction_q_3_response])
            /
            Count(total {<[Flag_DWS_PY]={1}>}[DWS_instruction_q_3_response])
            ) ,'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Instruction Q4 Most Positive (CY)',
        qDef: `=Num(
          if(${getSuppression('instruction', 'Teacher', 'cy', 4)},
            Sum({<[Flag_DWS_CY]={1}>}[DWS_instruction_q_4_ishighest])
            /
            Count({<[Flag_DWS_CY]={1}>}[DWS_instruction_q_4_response])
          ),'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Instruction Q4 Most Positive (PY)',
        qDef: `=Num(
          if(${getSuppression('instruction', 'Teacher', 'py', 4)},
            Sum({<[Flag_DWS_PY]={1}>}[DWS_instruction_q_4_ishighest])
            /
            Count({<[Flag_DWS_PY]={1}>}[DWS_instruction_q_4_response])
          ),'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Instruction Q4 (CY)',
        qDef: `=Num(
          if(${getSuppression('instruction', 'Teacher', 'cy', 4)},
            Count({<[Flag_DWS_CY]={1}>}[DWS_instruction_q_4_response])
            /
            Count(total {<[Flag_DWS_CY]={1}>}[DWS_instruction_q_4_response])
            ) ,'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Instruction Q4 (PY)',
        qDef: `=Num(
          if(${getSuppression('instruction', 'Teacher', 'py', 4)},
            Count({<[Flag_DWS_PY]={1}>}[DWS_instruction_q_4_response])
            /
            Count(total {<[Flag_DWS_PY]={1}>}[DWS_instruction_q_4_response])
            ) ,'##.0%')`
      }
    },

    {
      qDef: {
        qLabel: 'Parent - School Relationship Q1 Most Positive (CY)',
        qDef: `=Num(
          if(${getSuppression('community', 'Parent', 'cy', 1)},
            Sum({<[Flag_DWS_CY]={1}>}[DWS_community_q_1_ishighest])
            /
            Count({<[Flag_DWS_CY]={1}>}[DWS_community_q_1_response])
          ),'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Parent - School Relationship Q1 Most Positive (PY)',
        qDef: `=Num(
          if(${getSuppression('community', 'Parent', 'py', 1)},
            Sum({<[Flag_DWS_PY]={1}>}[DWS_community_q_1_ishighest])
            /
            Count({<[Flag_DWS_PY]={1}>}[DWS_community_q_1_response])
          ),'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Parent - School Relationship Q1 (CY)',
        qDef: `=Num(
          if(${getSuppression('community', 'Parent', 'cy', 1)},
            Count({<[Flag_DWS_CY]={1}>}[DWS_community_q_1_response])
            /
            Count(total {<[Flag_DWS_CY]={1}>}[DWS_community_q_1_response])
            ) ,'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Parent - School Relationship Q1 (PY)',
        qDef: `=Num(
          if(${getSuppression('community', 'Parent', 'py', 1)},
            Count({<[Flag_DWS_PY]={1}>}[DWS_community_q_1_response])
            /
            Count(total {<[Flag_DWS_PY]={1}>}[DWS_community_q_1_response])
            ) ,'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Parent - School Relationship Q2 Most Positive (CY)',
        qDef: `=Num(
          if(${getSuppression('community', 'Parent', 'cy', 2)},
            Sum({<[Flag_DWS_CY]={1}>}[DWS_community_q_2_ishighest])
            /
            Count({<[Flag_DWS_CY]={1}>}[DWS_community_q_2_response])
          ),'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Parent - School Relationship Q2 Most Positive (PY)',
        qDef: `=Num(
          if(${getSuppression('community', 'Parent', 'py', 2)},
            Sum({<[Flag_DWS_PY]={1}>}[DWS_community_q_2_ishighest])
            /
            Count({<[Flag_DWS_PY]={1}>}[DWS_community_q_2_response])
          ),'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Parent - School Relationship Q2 (CY)',
        qDef: `=Num(
          if(${getSuppression('community', 'Parent', 'cy', 2)},
            Count({<[Flag_DWS_CY]={1}>}[DWS_community_q_2_response])
            /
            Count(total {<[Flag_DWS_CY]={1}>}[DWS_community_q_2_response])
            ) ,'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Parent - School Relationship Q2 (PY)',
        qDef: `=Num(
          if(${getSuppression('community', 'Parent', 'py', 2)},
            Count({<[Flag_DWS_PY]={1}>}[DWS_community_q_2_response])
            /
            Count(total {<[Flag_DWS_PY]={1}>}[DWS_community_q_2_response])
            ) ,'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Parent - School Relationship Q3 Most Positive (CY)',
        qDef: `=Num(
          if(${getSuppression('community', 'Parent', 'cy', 3)},
            Sum({<[Flag_DWS_CY]={1}>}[DWS_community_q_3_ishighest])
            /
            Count({<[Flag_DWS_CY]={1}>}[DWS_community_q_3_response])
          ),'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Parent - School Relationship Q3 Most Positive (PY)',
        qDef: `=Num(
          if(${getSuppression('community', 'Parent', 'py', 3)},
            Sum({<[Flag_DWS_PY]={1}>}[DWS_community_q_3_ishighest])
            /
            Count({<[Flag_DWS_PY]={1}>}[DWS_community_q_3_response])
          ),'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Parent - School Relationship Q3 (CY)',
        qDef: `=Num(
          if(${getSuppression('community', 'Parent', 'cy', 3)},
            Count({<[Flag_DWS_CY]={1}>}[DWS_community_q_3_response])
            /
            Count(total {<[Flag_DWS_CY]={1}>}[DWS_community_q_3_response])
            ) ,'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Parent - School Relationship Q3 (PY)',
        qDef: `=Num(
          if(${getSuppression('community', 'Parent', 'py', 3)},
            Count({<[Flag_DWS_PY]={1}>}[DWS_community_q_3_response])
            /
            Count(total {<[Flag_DWS_PY]={1}>}[DWS_community_q_3_response])
            ) ,'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Parent - School Relationship Q3 (PY)',
        qDef: `=Num(
          if(${getSuppression('community', 'Parent', 'py', 3)},
            Count({<[Flag_DWS_PY]={1}>}[DWS_community_q_3_response])
            /
            Count(total {<[Flag_DWS_PY]={1}>}[DWS_community_q_3_response])
            ) ,'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Parent - School Relationship Q4 Most Positive (CY)',
        qDef: `=Num(
          if(${getSuppression('community', 'Parent', 'cy', 4)},
            Sum({<[Flag_DWS_CY]={1}>}[DWS_community_q_4_ishighest])
            /
            Count({<[Flag_DWS_CY]={1}>}[DWS_community_q_4_response])
          ),'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Parent - School Relationship Q4 Most Positive (PY)',
        qDef: `=Num(
          if(${getSuppression('community', 'Parent', 'py', 4)},
            Sum({<[Flag_DWS_PY]={1}>}[DWS_community_q_4_ishighest])
            /
            Count({<[Flag_DWS_PY]={1}>}[DWS_community_q_4_response])
          ),'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Parent - School Relationship Q4 (CY)',
        qDef: `=Num(
          if(${getSuppression('community', 'Parent', 'cy', 4)},
            Count({<[Flag_DWS_CY]={1}>}[DWS_community_q_4_response])
            /
            Count(total {<[Flag_DWS_CY]={1}>}[DWS_community_q_4_response])
            ) ,'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Parent - School Relationship Q4 (PY)',
        qDef: `=Num(
          if(${getSuppression('community', 'Parent', 'py', 4)},
            Count({<[Flag_DWS_PY]={1}>}[DWS_community_q_4_response])
            /
            Count(total {<[Flag_DWS_PY]={1}>}[DWS_community_q_4_response])
            ) ,'##.0%')`
      }
    },
    {
      qDef: {
        qLabel: 'Climate Avg Scaled Score (CY)',
        qDef: `=Sum({<topic={'School Climate'},survey={'Overall'},Flag_DWS_CY={1}>} topic_score)`
      }
    },
    {
      qDef: {
        qLabel: 'Climate Avg Scaled Score (PY)',
        qDef: `=Sum({<topic={'School Climate'},survey={'Overall'},Flag_DWS_PY={1}>} topic_score)`
      }
    },
    {
      qDef: {
        qLabel: 'Climate Suppression (CY)',
        qDef: `=If(
          RangeSum(
            if(${getSuppression('climate', 'Student', 'cy')}, 1, 0),
            if(${getSuppression('climate', 'Parent', 'cy')}, 1, 0),
            if(${getSuppression('climate', 'Teacher', 'cy')}, 1, 0)
          ) >= 2, 1, 0)`
      }
    },
    {
      qDef: {
        qLabel: 'Instruction Avg Scaled Score (CY)',
        qDef: `=Sum({<topic={'Instructional Environment'},survey={'Overall'},Flag_DWS_CY={1}>} topic_score)`
      }
    },
    {
      qDef: {
        qLabel: 'Instruction Avg Scaled Score (PY)',
        qDef: `=Sum({<topic={'Instructional Environment'},survey={'Overall'},Flag_DWS_PY={1}>} topic_score)`
      }
    },
    {
      qDef: {
        qLabel: 'Instruction Suppression (CY)',
        qDef: `=If(
          RangeSum(
            if(${getSuppression('instruction', 'Student', 'cy')}, 1, 0),
            if(${getSuppression('instruction', 'Parent', 'cy')}, 1, 0),
            if(${getSuppression('instruction', 'Teacher', 'cy')}, 1, 0)
          ) >= 2, 1, 0)`
      }
    },
    {
      qDef: {
        qLabel: 'Parent - School Relationship Avg Scaled Score (CY)',
        qDef: `=Sum({<topic={'Family Engagement'},survey={'Overall'},Flag_DWS_CY={1}>} topic_score)`
      }
    },
    {
      qDef: {
        qLabel: 'Parent - School Relationship Avg Scaled Score (PY)',
        qDef: `=Sum({<topic={'Family Engagement'},survey={'Overall'},Flag_DWS_PY={1}>} topic_score)`
      }
    },
    {
      qDef: {
        qLabel: 'Parent - School Relationship Suppression (CY)',
        qDef: `=if(${getSuppression('community', 'Parent', 'cy')}, 1, 0)`
      }
    }
  ],
  //18 measures
  enrollment: [
    {
      qDef: {
        qLabel: 'Enrollment Date',
        qDef:
          "='October 1, ' & Left(MaxString({<[Flag_Oct1_CY]={1}>}SchoolYear),4)"
      }
    },
    {
      qDef: {
        qLabel: '# Enrolled (Year to Year) (CY)',
        qDef: '=Sum({<[Flag_Oct1_CY]={1}>} Enrolled_Oct1)'
      }
    },
    {
      qDef: {
        qLabel: '# Enrolled (Year to Year) (PY)',
        qDef: '=Sum({<[Flag_Oct1_PY]={1}>} Enrolled_Oct1)'
      }
    },
    {
      qDef: {
        qLabel:
          '# District and Charter Students Living in Catchment (Year to Year) (CY)',
        qDef: '=Sum({<[Flag_Oct1_CY]={1}>} Enrolled_Oct1_Catchment_Denom)'
      }
    },
    {
      qDef: {
        qLabel:
          '# District and Charter Students Living in Catchment (Year to Year) (PY)',
        qDef: '=Sum({<[Flag_Oct1_PY]={1}>} Enrolled_Oct1_Catchment_Denom)'
      }
    },
    {
      qDef: {
        qLabel: '% of students reside in catchment',
        qDef: '=Sum({<[Flag_Oct1_CY]={1}>}[Enrolled_Oct1_InCatchment])'
      }
    },
    {
      qDef: {
        qLabel: '% of students reside in catchment - Denominator',
        qDef: '=Sum({<[Flag_Oct1_CY]={1}>}[Enrolled_Oct1])'
      }
    },
    {
      qDef: {
        qLabel: 'Percent of Students with Economically Disadvantage (YOY) (CY)',
        qDef: `=RangeMin(1, 1.6 * Sum({<[Sector]-={'Charter', 'Non Public Special Education', 'Pre-K', 'Other'},  [EconDisadvantaged]={'Yes'}, [Flag_Oct1_CY]={1}>} Enrolled_Oct1) /
            Sum({<[Sector]-={'Charter', 'Non Public Special Education', 'Pre-K', 'Other'}, [Flag_Oct1_CY]={1}>} Enrolled_Oct1))`
      }
    },
    {
      qDef: {
        qLabel:
          'Percent of Students with Economically Disadvantage (YOY) (CY) - Denominator',
        qDef: `=Sum({<[Sector]-={'Charter', 'Non Public Special Education', 'Pre-K', 'Other'}, [Flag_Oct1_CY]={1}>} Enrolled_Oct1)`
      }
    },
    {
      qDef: {
        qLabel: 'Percent of Students with Economically Disadvantage (YOY) (PY)',
        qDef: `=RangeMin(1, 1.6 * Sum({<[Sector]-={'Charter', 'Non Public Special Education', 'Pre-K', 'Other'},  [EconDisadvantaged]={'Yes'}, [Flag_Oct1_PY]={1}>} Enrolled_Oct1) /
            Sum({<[Sector]-={'Charter', 'Non Public Special Education', 'Pre-K', 'Other'},  [Flag_Oct1_PY]={1}>} Enrolled_Oct1))`
      }
    },
    {
      qDef: {
        qLabel:
          'Percent of Students with Economically Disadvantage (YOY) (PY) Denominator',
        qDef: `Sum({<[Sector]-={'Charter', 'Non Public Special Education', 'Pre-K', 'Other'},  [Flag_Oct1_PY]={1}>} Enrolled_Oct1)`
      }
    },
    {
      qDef: {
        qLabel: 'Percent of Special Education Students (YoY) (CY)',
        qDef: "=Sum({<[IEP]={'Yes'}, [Flag_Oct1_CY]={1}>} Enrolled_Oct1)"
      }
    },
    {
      qDef: {
        qLabel:
          'Percent of Special Education Students (YoY) (CY) - Denominator',
        qDef: '=Sum({<[Flag_Oct1_CY]={1}>} Enrolled_Oct1)'
      }
    },
    {
      qDef: {
        qLabel: 'Percent of Special Education Students (YoY) (PY)',
        qDef: "=Sum({<[IEP]={'Yes'}, [Flag_Oct1_PY]={1}>} Enrolled_Oct1)"
      }
    },
    {
      qDef: {
        qLabel:
          'Percent of Special Education Students (YoY) (PY) - Denominator',
        qDef: '=Sum({<[Flag_Oct1_PY]={1}>} Enrolled_Oct1)'
      }
    },
    {
      qDef: {
        qLabel: 'Percent of EL Students (YoY) (CY)',
        qDef:
          "=Sum({<[Sector]-={'Charter', 'Pre-K', 'Other'},  [EL]={'Yes'}, [Flag_Oct1_CY]={1}>} Enrolled_Oct1)"
      }
    },
    {
      qDef: {
        qLabel: 'Percent of EL Students (YoY) (CY) - Denominator',
        qDef: `=Sum({<[Sector]-={'Charter', 'Pre-K', 'Other'},  [Flag_Oct1_CY]={1}>} Enrolled_Oct1)`
      }
    },
    {
      qDef: {
        qLabel: 'Percent of EL Students (YoY) (PY)',
        qDef:
          "=Sum({<[Sector]-={'Charter', 'Pre-K', 'Other'},  [EL]={'Yes'}, [Flag_Oct1_PY]={1}>} Enrolled_Oct1)"
      }
    },
    {
      qDef: {
        qLabel: 'Percent of EL Students (YoY) (PY) - Denominator',
        qDef: `=Sum({<[Sector]-={'Charter', 'Pre-K', 'Other'},  [Flag_Oct1_PY]={1}>} Enrolled_Oct1)`
      }
    }
  ],
  //12 measures
  reading: [
    {
      qDef: {
        qLabel: 'PSSA ELA (Grade 3): Percentage Proficient or Advanced (CY)',
        qDef: '=Sum({<[Flag_StudentMetrics_CY]={1}>} PSSA_ELA_G3_ProfAdv)'
      }
    },
    {
      qDef: {
        qLabel:
          'PSSA ELA (Grade 3): Percentage Proficient or Advanced (CY) - Count',
        qDef: '=Count({<[Flag_StudentMetrics_CY]={1}>} PSSA_ELA_G3_ProfAdv)'
      }
    },
    {
      qDef: {
        qLabel: 'PSSA ELA (Grade 3): Percentage Proficient or Advanced (PY)',
        qDef: '=Sum({<[Flag_StudentMetrics_CY]={1}>} PSSA_ELA_G3_ProfAdv)'
      }
    },
    {
      qDef: {
        qLabel:
          'PSSA ELA (Grade 3): Percentage Proficient or Advanced (PY) - Count',
        qDef: '=Count({<[Flag_StudentMetrics_CY]={1}>} PSSA_ELA_G3_ProfAdv)'
      }
    },
    {
      qDef: {
        qLabel: 'Keystone_ELA_ProfAdv Sum (CY)',
        qDef: '=Sum({<[Flag_StudentMetrics_CY]={1}>} Keystone_ELA_ProfAdv)'
      }
    },
    {
      qDef: {
        qLabel: 'Keystone_ELA_ProfAdv Count (CY)',
        qDef: '=Count({<[Flag_StudentMetrics_CY]={1}>} Keystone_ELA_ProfAdv)'
      }
    },
    {
      qDef: {
        qLabel: 'Keystone_ELA_ProfAdv Sum (PY)',
        qDef: '=Sum({<[Flag_StudentMetrics_PY]={1}>} Keystone_ELA_ProfAdv)'
      }
    },
    {
      qDef: {
        qLabel: 'Keystone_ELA_ProfAdv Count (PY)',
        qDef: '=Count({<[Flag_StudentMetrics_PY]={1}>} Keystone_ELA_ProfAdv)'
      }
    },
    {
      qDef: {
        qLabel: 'PSSA_ELA_ProfAdv Sum (CY)',
        qDef: '=Sum({<[Flag_StudentMetrics_CY]={1}>} PSSA_ELA_ProfAdv)'
      }
    },
    {
      qDef: {
        qLabel: 'PSSA_ELA_ProfAdv Count (CY)',
        qDef: '=Count({<[Flag_StudentMetrics_CY]={1}>} PSSA_ELA_ProfAdv)'
      }
    },
    {
      qDef: {
        qLabel: 'PSSA_ELA_ProfAdv Sum (PY)',
        qDef: '=Sum({<[Flag_StudentMetrics_PY]={1}>} PSSA_ELA_ProfAdv)'
      }
    },
    {
      qDef: {
        qLabel: 'PSSA_ELA_ProfAdv Count (PY)',
        qDef: '=Count({<[Flag_StudentMetrics_PY]={1}>} PSSA_ELA_ProfAdv)'
      }
    }
  ]
}
logger.debug('kpimeasure exported')

export default kpiMeasures
